(() => {
  const SEARCH_PATH = '/search';
  const GENERATED_FILTER = 'data-jb-precise-search-filter';
  const DIMENSION_PARAM = 'jb_dimension';

  const stripQuotes = (value) => {
    const trimmed = String(value || '').trim();
    if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) return trimmed.slice(1, -1).trim();
    return trimmed;
  };

  const normalizedNumber = (value) => {
    const number = Number.parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(number)) return String(value);
    return Number.isInteger(number) ? String(number) : String(number).replace(/0+$/, '').replace(/\.$/, '');
  };

  const canonicalBoltPattern = (lugsValue, pcdValue) => {
    const lugs = Number.parseInt(lugsValue, 10);
    const pcd = Number.parseFloat(String(pcdValue).replace(',', '.'));
    if (lugs < 3 || lugs > 10 || !Number.isFinite(pcd)) return null;

    const entered = `${lugs}x${normalizedNumber(pcdValue)}`;
    const metricAliases = {
      '5x114': '5x114.3',
      '5x139': '5x139.7',
      '6x139': '6x139.7',
      '8x165': '8x165.1',
    };
    if (pcd >= 70 && pcd <= 300) return metricAliases[entered] || entered;

    const imperialAliases = {
      '4x4.25': '4x108',
      '4x4.5': '4x114.3',
      '5x4.25': '5x108',
      '5x4.5': '5x114.3',
      '5x4.75': '5x120.7',
      '5x5': '5x127',
      '5x5.5': '5x139.7',
      '6x4.5': '6x114.3',
      '6x5.5': '6x139.7',
      '8x6.5': '8x165.1',
    };
    return imperialAliases[entered] || null;
  };

  const buildTireSize = (value) => {
    let compact = stripQuotes(value).replace(/\s+/g, '');
    const metricAlias = compact.match(/^(LT|P|ST)?(\d{3})(?:[\/-]?)(\d{2})(?:[\/-]|ZR?|R)?(\d{2})(?:LT)?$/i);
    if (metricAlias) compact = (metricAlias[1] || '') + metricAlias[2] + '/' + metricAlias[3] + 'R' + metricAlias[4];
    const flotationAlias = compact.match(/^(?:LT)?(\d{2})(\d{2})(\d{2})(\d{2})(?:LT)?$/i);
    if (flotationAlias) compact = flotationAlias[1] + 'x' + flotationAlias[2] + '.' + flotationAlias[3] + 'R' + flotationAlias[4];

    let match = compact.match(/^(?:(LT|P|ST))?(\d{3})\/(\d{2,3})[Rr](\d{2})(?:LT)?$/i);
    if (match) {
      if (+match[2] < 125 || +match[2] > 455 || +match[3] < 20 || +match[3] > 100 || +match[4] < 10 || +match[4] > 30) return null;
      const prefix = (match[1] || '').toUpperCase();
      const width = match[2];
      const profile = match[3];
      const rim = match[4];
      return {
        kind: 'tire',
        query: `${prefix}${width}/${profile}R${rim}`,
        filters: {
          'filter.p.m.custom.largeur_pneu': width,
          'filter.p.m.custom.profil_pneu': profile,
          'filter.p.m.custom.diametre_pneu': rim,
        },
      };
    }

    match = compact.match(/^(?:LT)?(\d{2,3}(?:[.,]\d+)?)[xX×](\d{1,2}(?:[.,]\d+)?)[Rr-]?(\d{2})(?:LT)?$/i);
    if (!match) return null;
    const overall = Number.parseFloat(match[1].replace(',', '.'));
    const width = Number.parseFloat(match[2].replace(',', '.'));
    const rim = Number.parseInt(match[3], 10);
    if (overall < 25 || overall > 60 || width < 7 || width > 20 || rim < 10 || rim > 30) return null;
    const canonicalOverall = normalizedNumber(match[1]);
    const canonicalWidth = normalizedNumber(match[2]);
    return {
      kind: 'tire',
      query: `${canonicalOverall}x${canonicalWidth}R${rim}`,
      filters: {
        'filter.p.m.custom.diametre_exterieur_pneu': canonicalOverall,
        'filter.p.m.custom.largeur_pneu': canonicalWidth,
        'filter.p.m.custom.diametre_pneu': String(rim),
      },
    };
  };

  const removeSpans = (text, spans) => {
    let result = text;
    [...spans].sort((a, b) => b.start - a.start).forEach(({ start, end }) => {
      result = result.slice(0, start) + ' ' + result.slice(end);
    });
    return result.replace(/\s+/g, ' ').trim();
  };

  const cleanGenericWords = (text, kind) => {
    const pattern = kind === 'tire' ? /\b(pneus?|tires?)\b/gi : /\b(mags?|roues?|wheels?|rims?)\b/gi;
    return text.replace(pattern, ' ').replace(/\s+/g, ' ').trim();
  };

  const parseRequest = (value) => {
    const raw = stripQuotes(value);
    if (!raw) return null;

    const wholeTire = buildTireSize(raw);
    if (wholeTire) return { ...wholeTire, textQuery: wholeTire.query, displayQuery: wholeTire.query };

    const tireCandidates = Array.from(raw.matchAll(/(?:^|\s)((?:(?:LT|P|ST)?\d{3}[\/-]?\d{2}(?:[\/-]|Z?R)?\d{2}(?:LT)?|(?:LT)?\d{8}(?:LT)?|(?:LT)?\d{2,3}(?:[.,]\d+)?[x×]\d{1,2}(?:[.,]\d+)?[R-]?\d{2}(?:LT)?))(?=\s|$)/gi));
    const validTires = tireCandidates.map((match) => ({ match, size: buildTireSize(match[1]) })).filter((item) => item.size);
    if (validTires.length === 1) {
      const item = validTires[0];
      const leadingSpace = item.match[0].length - item.match[1].length;
      const start = item.match.index + leadingSpace;
      const rest = cleanGenericWords(removeSpans(raw, [{ start, end: start + item.match[1].length }]), 'tire');
      return {
        ...item.size,
        textQuery: rest ? `${rest} ${item.size.query}` : item.size.query,
        displayQuery: raw,
      };
    }
    if (validTires.length > 1) return null;

    const spans = [];
    const bolts = [];
    const wheelSizes = [];
    const pairRegex = /(^|\s)(\d{1,2})\s*[x×]\s*(\d{1,3}(?:[.,]\d+)?)(?=\s|$)/gi;
    for (const match of raw.matchAll(pairRegex)) {
      const first = Number.parseInt(match[2], 10);
      const second = Number.parseFloat(match[3].replace(',', '.'));
      const leadingSpace = match[1].length;
      const start = match.index + leadingSpace;
      const end = match.index + match[0].length;
      const bolt = canonicalBoltPattern(match[2], match[3]);
      if (bolt) {
        bolts.push(bolt);
        spans.push({ start, end });
      } else if (first >= 12 && first <= 30 && second >= 4 && second <= 20) {
        wheelSizes.push({ diameter: String(first), width: normalizedNumber(match[3]), canonical: `${first}x${normalizedNumber(match[3])}` });
        spans.push({ start, end });
      }
    }

    const partialDiameters = [];
    const partialRegex = /(^|\s)(1[2-9]|2\d|30)\s*[x×](?=\s|$)/gi;
    for (const match of raw.matchAll(partialRegex)) {
      const leadingSpace = match[1].length;
      const start = match.index + leadingSpace;
      const end = match.index + match[0].length;
      if (spans.some((span) => start < span.end && end > span.start)) continue;
      partialDiameters.push(String(Number.parseInt(match[2], 10)));
      spans.push({ start, end });
    }

    if (new Set(bolts).size > 1 || wheelSizes.length > 1 || new Set(partialDiameters).size > 1) return null;
    const bolt = bolts[0] || null;
    const wheelSize = wheelSizes[0] || null;
    const partialDiameter = partialDiameters[0] || null;
    if (!bolt && !wheelSize && !partialDiameter) return null;
    if (wheelSize && partialDiameter && wheelSize.diameter !== partialDiameter) return null;

    const diameter = wheelSize ? wheelSize.diameter : partialDiameter;
    const filters = {};
    if (bolt) filters['filter.p.m.custom.entraxe_roue'] = bolt;
    if (diameter) filters['filter.p.m.custom.diametre_roue'] = diameter;
    if (wheelSize) filters['filter.p.m.custom.largeur_roue'] = wheelSize.width;

    const rest = cleanGenericWords(removeSpans(raw, spans), 'wheel');
    const primary = bolt || (wheelSize && wheelSize.canonical) || `${diameter}x`;
    const structural = [bolt, wheelSize && wheelSize.canonical, !wheelSize && partialDiameter ? `${partialDiameter}x` : null].filter(Boolean).join(' ');
    return {
      kind: 'wheel',
      query: structural,
      filters,
      rest,
      bolt,
      wheelSize,
      diameter,
      textQuery: [rest, primary].filter(Boolean).join(' '),
      displayQuery: raw,
    };
  };

  const titleSizeQuery = (precise) => {
    if (precise.kind === 'wheel') {
      const terms = [];
      if (precise.wheelSize) terms.push('title:"' + precise.wheelSize.canonical + '"');
      else if (precise.diameter) {
        const aliases = [];
        for (let width = 4; width <= 20; width += 0.5) {
          aliases.push('title:"' + precise.diameter + 'x' + normalizedNumber(width) + '"');
        }
        terms.push('(' + aliases.join(' OR ') + ')');
      }
      if (precise.bolt) terms.push('title:"' + precise.bolt + '"');
      return (precise.rest ? '(' + precise.rest + ') AND ' : '') + terms.join(' AND ');
    }
    const filters = precise.filters;
    const width = filters['filter.p.m.custom.largeur_pneu'];
    const rim = filters['filter.p.m.custom.diametre_pneu'];
    const overall = filters['filter.p.m.custom.diametre_exterieur_pneu'];
    const bases = new Set();
    if (overall) {
      for (const widthAlias of new Set([width, Number(width).toFixed(2)])) {
        for (const separator of ['x', '×']) bases.add(overall + separator + widthAlias + 'R' + rim);
      }
    } else {
      const profile = filters['filter.p.m.custom.profil_pneu'];
      for (const radial of ['R', 'ZR']) bases.add(width + '/' + profile + radial + rim);
    }
    const aliases = new Set();
    for (const base of bases) {
      for (const prefix of ['', 'LT', ...(overall ? [] : ['P', 'ST'])]) {
        for (const suffix of ['', 'LT']) aliases.add(prefix + base + suffix);
      }
    }
    const terms = Array.from(aliases, (size) => 'title:"' + size + '"').join(' OR ');
    const parsedRest = cleanGenericWords(precise.textQuery.replace(precise.query, ''), 'tire');
    return (parsedRest ? '(' + parsedRest + ') AND ' : '') + '(' + terms + ')';
  };

  const appendHiddenInput = (form, name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute(GENERATED_FILTER, '');
    form.appendChild(input);
  };

  const preciseStyle = document.createElement('style');
  preciseStyle.textContent = '.jb-precise-search [data-predictive-search]{display:none!important}.jb-precise-search-hint{display:block;margin-top:8px}';
  document.head.appendChild(preciseStyle);

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
    const form = input.closest('form');
    if (!form || form.id === 'FacetsFilterForm') return;
    const request = parseRequest(input.value);
    form.classList.toggle('jb-precise-search', Boolean(request));
    let hint = form.querySelector('.jb-precise-search-hint');
    if (!request) {
      if (hint) hint.remove();
      return;
    }
    event.stopImmediatePropagation();
    input.setAttribute('aria-expanded', 'false');
    if (!hint) {
      hint = document.createElement('button');
      hint.type = 'submit';
      hint.className = 'button-text-link jb-precise-search-hint';
      form.appendChild(hint);
    }
    hint.textContent = request.kind === 'tire' ? 'Voir les pneus exacts : ' + request.displayQuery : 'Voir les roues compatibles : ' + request.displayQuery;
  }, true);

  const prepareForm = (form) => {
    if (form.id === 'FacetsFilterForm') return;
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    const precise = parseRequest(input.value);
    form.querySelectorAll('[' + GENERATED_FILTER + ']').forEach((element) => element.remove());
    if (!precise) return;
    input.value = titleSizeQuery(precise);
    appendHiddenInput(form, DIMENSION_PARAM, precise.displayQuery);
    if (!form.querySelector('input[name="type"]')) appendHiddenInput(form, 'type', 'product');
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action || window.location.href, window.location.origin);
    if (action.pathname === SEARCH_PATH) prepareForm(form);
  }, true);

  if (window.location.pathname !== SEARCH_PATH) return;
  const url = new URL(window.location.href);
  const currentQuery = url.searchParams.get('q') || '';
  const savedDimension = url.searchParams.get(DIMENSION_PARAM);
  const precise = parseRequest(savedDimension || currentQuery);
  if (!precise) return;

  const executableQuery = titleSizeQuery(precise);
  if (!savedDimension || currentQuery !== executableQuery || url.searchParams.has('jb_size_mode')) {
    url.searchParams.set('q', executableQuery);
    url.searchParams.set('type', 'product');
    url.searchParams.set(DIMENSION_PARAM, precise.displayQuery);
    if (!savedDimension || currentQuery !== executableQuery) {
      Object.keys(precise.filters).forEach((name) => url.searchParams.delete(name));
    }
    url.searchParams.delete('jb_size_mode');
    url.searchParams.delete('page');
    window.location.replace(url.toString());
    return;
  }

  const facets = document.getElementById('FacetsFilterForm');
  if (facets && !facets.querySelector('input[name="' + DIMENSION_PARAM + '"]')) appendHiddenInput(facets, DIMENSION_PARAM, precise.displayQuery);
  document.querySelectorAll('input[name="q"]:not([type="hidden"])').forEach((input) => { input.value = precise.displayQuery; });
  const count = document.getElementById('ProductCount');
  if (count) count.textContent = count.textContent.replace('“' + currentQuery + '”', '“' + precise.displayQuery + '”');
  document.querySelectorAll('.page-hero-caption').forEach((caption) => {
    const walker = document.createTreeWalker(caption, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) walker.currentNode.textContent = walker.currentNode.textContent.replace(currentQuery, precise.displayQuery);
  });
  document.title = 'Recherche : ' + precise.displayQuery + ' – JB Performance';

  document.querySelectorAll('a[href]').forEach((link) => {
    const next = new URL(link.href, location.origin);
    if (next.origin !== location.origin || next.pathname !== SEARCH_PATH || next.searchParams.get('q') !== currentQuery) return;
    next.searchParams.set('q', executableQuery);
    next.searchParams.set(DIMENSION_PARAM, precise.displayQuery);
    next.searchParams.delete('jb_size_mode');
    link.href = next.toString();
  });
})();